import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { formatDateTime } from '@/lib/time';

import { kindLabel, statusBadge } from '../presentation';
import type { DocumentRequest } from '../types';

// Datos de una solicitud en solo lectura (G3-1): qué se pide, quién, cuándo y por qué;
// si ya se resolvió, quién la resolvió, la nota o el motivo del rechazo y el documento
// resultante. Cero valores internos (UX-COPY-01).

const name = (p: { displayName: string | null } | null): string =>
  p === null ? 'Sin asignar' : (p.displayName ?? 'Persona sin nombre');

function Kv({ label, children }: { label: string; children: ReactElement | string }): ReactElement {
  return (
    <div className="kv">
      <dt className="kk">{label}</dt>
      <dd className="vv">{children}</dd>
    </div>
  );
}

export function RequestSummary({
  request: r,
  showProposal,
}: {
  request: DocumentRequest;
  /** Tipo, proceso, nombre y dueño propuestos (se ocultan cuando Calidad los edita). */
  showProposal: boolean;
}): ReactElement {
  const badge = statusBadge(r.status);
  return (
    <>
      <dl className="kvs">
        <Kv label="Solicitud">
          {r.kind === 'modificacion' && r.target !== null
            ? `${kindLabel(r.kind)} · ${r.target.code}`
            : kindLabel(r.kind)}
        </Kv>
        <Kv label="Estado">
          <span className={`badge ${badge.cls}`}>{badge.label}</span>
        </Kv>
        <Kv label="Solicitante">{name(r.requester)}</Kv>
        <Kv label="Fecha">{formatDateTime(r.createdAt)}</Kv>
        {showProposal && (
          <>
            <Kv label="Nombre">{r.proposedTitle}</Kv>
            <Kv label="Tipo">{r.documentType.name}</Kv>
            <Kv label="Proceso">{r.processArea.name}</Kv>
            <Kv label="Dueño de proceso">{name(r.owner)}</Kv>
          </>
        )}
        {r.resolver !== null && r.resolvedAt !== null && (
          <>
            <Kv label="Resuelta por">{name(r.resolver)}</Kv>
            <Kv label="Resuelta el">{formatDateTime(r.resolvedAt)}</Kv>
          </>
        )}
        {r.result !== null && (
          <Kv label="Documento">
            <Link to={`/documentos/${r.result.id}`}>Ver {r.result.code}</Link>
          </Kv>
        )}
      </dl>
      <div className="fld">
        <span className="fld-lab">Motivo</span>
        <p className="ev-text">{r.reason}</p>
      </div>
      {r.resolutionNote !== null && (
        <div className="fld">
          <span className="fld-lab">
            {r.status === 'rechazada' ? 'Motivo del rechazo' : 'Nota de la resolución'}
          </span>
          <p className="ev-text">{r.resolutionNote}</p>
        </div>
      )}
    </>
  );
}
